import type { TimelineAlign, TimelineEvent, TimelineProps } from "@emkit/builder-widgets";
import { useEditor } from "../../store.js";
import { ColorField, SelectField, TextField } from "./fields.js";
import type { WidgetInspectorProps } from "./registry.js";

const ALIGN = ["left", "alternating"] as const;

function newId(): string {
  return `e-${Math.random().toString(36).slice(2, 8)}`;
}

export function TimelineInspector({ widget }: WidgetInspectorProps) {
  const update = useEditor((s) => s.updateWidgetProps);
  const p = widget.props as TimelineProps;
  const events = p.events ?? [];

  const setEvents = (next: TimelineEvent[]) => update(widget.id, { events: next });
  const addEvent = () =>
    setEvents([
      ...events,
      {
        id: newId(),
        date: "2026",
        title: "New milestone",
        description: "",
      },
    ]);
  const updateEvent = (id: string, patch: Partial<TimelineEvent>) =>
    setEvents(events.map((ev) => (ev.id === id ? { ...ev, ...patch } : ev)));
  const removeEvent = (id: string) => setEvents(events.filter((ev) => ev.id !== id));
  const moveEvent = (id: string, dir: "up" | "down") => {
    const idx = events.findIndex((ev) => ev.id === id);
    if (idx < 0) return;
    const target = dir === "up" ? idx - 1 : idx + 1;
    if (target < 0 || target >= events.length) return;
    const next = events.slice();
    const moved = next.splice(idx, 1)[0];
    if (!moved) return;
    next.splice(target, 0, moved);
    setEvents(next);
  };

  return (
    <>
      <SelectField
        label="Align"
        value={p.align ?? "left"}
        options={ALIGN}
        onChange={(v) => update(widget.id, { align: v as TimelineAlign })}
      />
      <ColorField
        label="Accent (dots + dates)"
        value={p.accent ?? "#111827"}
        onChange={(v) => update(widget.id, { accent: v })}
      />
      <ColorField
        label="Card background"
        value={p.cardBackground ?? "#ffffff"}
        onChange={(v) => update(widget.id, { cardBackground: v })}
      />
      <ColorField
        label="Text"
        value={p.textColor ?? "#0f172a"}
        onChange={(v) => update(widget.id, { textColor: v })}
      />

      <div className="emke-section-title" style={{ marginTop: 8 }}>
        Events ({events.length})
      </div>

      {events.map((ev, i) => (
        <div
          key={ev.id}
          style={{
            padding: "8px 16px 10px",
            borderBottom: "1px solid var(--border)",
          }}
        >
          <div
            style={{
              display: "flex",
              gap: 4,
              justifyContent: "flex-end",
              marginBottom: 4,
            }}
          >
            <button
              type="button"
              className="emke-btn"
              onClick={() => moveEvent(ev.id, "up")}
              disabled={i === 0}
              style={{ padding: "2px 6px", opacity: i === 0 ? 0.4 : 1 }}
            >
              ↑
            </button>
            <button
              type="button"
              className="emke-btn"
              onClick={() => moveEvent(ev.id, "down")}
              disabled={i === events.length - 1}
              style={{
                padding: "2px 6px",
                opacity: i === events.length - 1 ? 0.4 : 1,
              }}
            >
              ↓
            </button>
            <button
              type="button"
              className="emke-btn is-danger"
              onClick={() => removeEvent(ev.id)}
              style={{ padding: "2px 6px" }}
            >
              ✕
            </button>
          </div>
          <TextField
            label={`Date ${i + 1}`}
            value={ev.date}
            onChange={(v) => updateEvent(ev.id, { date: v })}
          />
          <TextField
            label="Title"
            value={ev.title}
            onChange={(v) => updateEvent(ev.id, { title: v })}
          />
          <TextField
            label="Description"
            value={ev.description ?? ""}
            multiline
            onChange={(v) => updateEvent(ev.id, { description: v || undefined })}
          />
        </div>
      ))}

      <div style={{ padding: "12px 16px 0" }}>
        <button
          type="button"
          className="emke-btn"
          onClick={addEvent}
          style={{ width: "100%" }}
        >
          + Add event
        </button>
      </div>
    </>
  );
}
